import {
	EBusinessEntity,
	ECitizenshipStatus,
	EDebtGuarantorType,
	EDebtPurposeType,
	EFicoScoreType,
	EFundingPurposeType,
	EIndustryExperienceType,
	EIndustryType,
	EInvestmentSourceType,
	EMaritalStatusType,
	ERealEstateType,
	ETimeframeType,
	EUsaState,
	TFormSelectOption,
} from '@/types/formFieldTypes';

const USA_STATE_OPTIONS: { label: string; value: EUsaState }[] = [
	{ label: 'Alabama', value: EUsaState.AL },
	{ label: 'Alaska', value: EUsaState.AK },
	{ label: 'Arizona', value: EUsaState.AZ },
	{ label: 'Arkansas', value: EUsaState.AR },
	{ label: 'California', value: EUsaState.CA },
	{ label: 'Colorado', value: EUsaState.CO },
	{ label: 'Connecticut', value: EUsaState.CT },
	{ label: 'Delaware', value: EUsaState.DE },
	{ label: 'District of Columbia', value: EUsaState.DC },
	{ label: 'Florida', value: EUsaState.FL },
	{ label: 'Georgia', value: EUsaState.GA },
	{ label: 'Hawaii', value: EUsaState.HI },
	{ label: 'Idaho', value: EUsaState.ID },
	{ label: 'Illinois', value: EUsaState.IL },
	{ label: 'Indiana', value: EUsaState.IN },
	{ label: 'Iowa', value: EUsaState.IA },
	{ label: 'Kansas', value: EUsaState.KS },
	{ label: 'Kentucky', value: EUsaState.KY },
	{ label: 'Louisiana', value: EUsaState.LA },
	{ label: 'Maine', value: EUsaState.ME },
	{ label: 'Maryland', value: EUsaState.MD },
	{ label: 'Massachusetts', value: EUsaState.MA },
	{ label: 'Michigan', value: EUsaState.MI },
	{ label: 'Minnesota', value: EUsaState.MN },
	{ label: 'Mississippi', value: EUsaState.MS },
	{ label: 'Missouri', value: EUsaState.MO },
	{ label: 'Montana', value: EUsaState.MT },
	{ label: 'Nebraska', value: EUsaState.NE },
	{ label: 'Nevada', value: EUsaState.NV },
	{ label: 'New Hampshire', value: EUsaState.NH },
	{ label: 'New Jersey', value: EUsaState.NJ },
	{ label: 'New Mexico', value: EUsaState.NM },
	{ label: 'New York', value: EUsaState.NY },
	{ label: 'North Carolina', value: EUsaState.NC },
	{ label: 'North Dakota', value: EUsaState.ND },
	{ label: 'Ohio', value: EUsaState.OH },
	{ label: 'Oklahoma', value: EUsaState.OK },
	{ label: 'Oregon', value: EUsaState.OR },
	{ label: 'Pennsylvania', value: EUsaState.PA },
	{ label: 'Puerto Rico', value: EUsaState.PR },
	{ label: 'Rhode Island', value: EUsaState.RI },
	{ label: 'South Carolina', value: EUsaState.SC },
	{ label: 'South Dakota', value: EUsaState.SD },
	{ label: 'Tennessee', value: EUsaState.TN },
	{ label: 'Texas', value: EUsaState.TX },
	{ label: 'Utah', value: EUsaState.UT },
	{ label: 'Vermont', value: EUsaState.VT },
	{ label: 'Virginia', value: EUsaState.VA },
	{ label: 'Washington', value: EUsaState.WA },
	{ label: 'West Virginia', value: EUsaState.WV },
	{ label: 'Wisconsin', value: EUsaState.WI },
	{ label: 'Wyoming', value: EUsaState.WY },
];

const USA_STATE_OPTIONS_MAP = USA_STATE_OPTIONS.reduce<TFormSelectOption>(
	(acc, { value, label }) => {
		acc[value] = label;
		return acc;
	},
	{} as TFormSelectOption,
);

const TIMEFRAME_TYPE_OPTIONS = [
	{ label: 'Immediately', value: ETimeframeType.immediately },
	{ label: 'Within 2-4 Weeks', value: ETimeframeType.TwoForWeeks },
	{ label: '4 Weeks or more', value: ETimeframeType.FourWeeksPlus },
];

const YES_OR_NO_TYPE_OPTIONS = [
	{ label: 'Yes', value: true },
	{ label: 'No', value: false },
];

const BUSINESS_ENTITY_OPTIONS: { label: string; value: EBusinessEntity }[] = [
	{
		label: 'C-Corp',
		value: EBusinessEntity.CCorp,
	},
	{
		label: 'S-Corp',
		value: EBusinessEntity.SCorp,
	},
	{
		label: 'LLC',
		value: EBusinessEntity.LLC,
	},
	{
		label: 'Sole Proprietor',
		value: EBusinessEntity.SoleProprietor,
	},
];

const INDUSTRY_OPTIONS: { label: string; value: EIndustryType }[] = [
	{ label: 'Agriculture, Forestry, Fishing and Hunting', value: EIndustryType.Agriculture },
	{ label: 'Mining', value: EIndustryType.Mining },
	{ label: 'Utilities', value: EIndustryType.Utilities },
	{ label: 'Construction', value: EIndustryType.Construction },
	{ label: 'Manufacturing', value: EIndustryType.Manufacturing },
	{ label: 'Wholesale Trade', value: EIndustryType.WholesaleTrade },
	{ label: 'Retail Trade', value: EIndustryType.RetailTrade },
	{ label: 'Transportation and Warehousing', value: EIndustryType.Transportation },
	{ label: 'Information', value: EIndustryType.Information },
	{ label: 'Finance and Insurance', value: EIndustryType.FinanceAndInsurance },
	{ label: 'Real Estate and Rental and Leasing', value: EIndustryType.RealEstateRentalAndLeasing },
	{ label: 'Professional, Scientific, and Technical Services', value: EIndustryType.Services },
	{ label: 'Management of Companies and Enterprises', value: EIndustryType.Management },
	{
		label: 'Administrative and Support and Waste Management and Remediation Services',
		value: EIndustryType.AdministrativeAndSupport,
	},
	{ label: 'Educational Services', value: EIndustryType.EducationalServices },
	{ label: 'Health Care and Social Assistance', value: EIndustryType.HealthCare },
	{ label: 'Arts, Entertainment, and Recreation', value: EIndustryType.ArtsAndEntertainment },
	{ label: 'Accommodation and Food Services', value: EIndustryType.AccommodationAndFoodServices },
	{ label: 'Other Services (except Public Administration)', value: EIndustryType.OtherServices },
	{ label: 'Public Administration', value: EIndustryType.PublicAdministration },
];

const INDUSTRY_OPTIONS_MAP = INDUSTRY_OPTIONS.reduce<TFormSelectOption>((acc, { value, label }) => {
	acc[value] = label;
	return acc;
}, {} as TFormSelectOption);

const FICO_SCORE_TYPE_OPTIONS = [
	{ label: '800-850 (Excellent)', value: EFicoScoreType.Excellent },
	{ label: '740-799 (Very Good)', value: EFicoScoreType.Good },
	{ label: '670-739 (Good)', value: EFicoScoreType.Average },
	{ label: '640-669 (Fair)', value: EFicoScoreType.Fair },
	{ label: '580-639 (Poor)', value: EFicoScoreType.Poor },
	{ label: '300-579 (Very Poor)', value: EFicoScoreType.VeryPoor },
];

const FICO_SCORE_TYPE_OPTIONS_MAP = FICO_SCORE_TYPE_OPTIONS.reduce<TFormSelectOption>(
	(acc, { value, label }) => {
		acc[value] = label;
		return acc;
	},
	{} as TFormSelectOption,
);

const FUNDING_PURPOSE_OPTIONS = [
	{ label: 'Start a Business', value: EFundingPurposeType.StartBusiness },
	{ label: 'Buy a Business', value: EFundingPurposeType.BuyBusiness },
	{
		label: 'Buy Commercial Real Estate',
		value: EFundingPurposeType.BuyCommercialRealEstate,
	},
	{ label: 'Working Capital', value: EFundingPurposeType.WorkingCapital },
	{ label: 'Debt Refinance', value: EFundingPurposeType.DebtRefinancing },
	{ label: 'Other', value: EFundingPurposeType.Other },
];

const MARTIAL_STATUS_OPTIONS = [
	{ label: 'Married', value: EMaritalStatusType.Married },
	{ label: 'Unmarried', value: EMaritalStatusType.Unmarried },
	{ label: 'Separated', value: EMaritalStatusType.Separated },
];

const REAL_ESTATE_OPTIONS = [
	{ label: 'Existing Property', value: ERealEstateType.Existing },
	{ label: 'New Construction', value: ERealEstateType.New },
];

const CITIZENSHIP_STATUS_OPTIONS = [
	{ label: 'US Citizen', value: ECitizenshipStatus.USCitizen },
	{
		label: 'Lawful Permanent Resident (Green Card Holder)',
		value: ECitizenshipStatus.LawfulPermanentResident,
	},
	{ label: 'Other', value: ECitizenshipStatus.Other },
];

export const INVESTMENT_SOURCE_OPTIONS = [
	{ label: 'My own funds', value: EInvestmentSourceType.Onw },
	{ label: 'Gift from a family member', value: EInvestmentSourceType.Gift },
	{ label: 'Equity investor', value: EInvestmentSourceType.EquityInvestor },
	{ label: 'I don’t have it yet', value: EInvestmentSourceType.NoFunds },
];

export const INVESTMENT_SOURCE_OPTIONS_MAP = INVESTMENT_SOURCE_OPTIONS.reduce<TFormSelectOption>(
	(acc, { value, label }) => {
		acc[value] = label;
		return acc;
	},
	{} as TFormSelectOption,
);

export const INDUSTRY_EXPERIENCE_OPTIONS = [
	{
		label: 'Yes - I have direct experience in this industry',
		value: EIndustryExperienceType.Direct,
	},
	{
		label: 'No – but I have relevant transferrable experience',
		value: EIndustryExperienceType.Transferable,
	},
	{ label: 'No - this would be new to me', value: EIndustryExperienceType.New },
];

export const INDUSTRY_EXPERIENCE_OPTIONS_MAP =
	INDUSTRY_EXPERIENCE_OPTIONS.reduce<TFormSelectOption>((acc, { value, label }) => {
		acc[value] = label;
		return acc;
	}, {} as TFormSelectOption);

export const DEBT_PURPOSE_OPTIONS = [
	{ label: 'Equipment Purchase', value: EDebtPurposeType.EquipmentPurchase },
	{ label: 'Working Capital', value: EDebtPurposeType.WorkingCapital },
	{ label: 'Debt Refinance', value: EDebtPurposeType.DebtRefinance },
	{ label: 'Real Estate', value: EDebtPurposeType.RealEstate },
	{ label: 'Other', value: EDebtPurposeType.Other },
];

export const DEBT_GUARANTOR_OPTIONS = [
	{ label: 'Equipment', value: EDebtGuarantorType.Equipment },
	{ label: 'Real Estate', value: EDebtGuarantorType.RealEstate },
	{ label: 'A / R', value: EDebtGuarantorType.AccountsReceivable },
	{ label: 'Unsecured', value: EDebtGuarantorType.Unsecured },
	{ label: 'Other', value: EDebtGuarantorType.Other },
];

export {
	USA_STATE_OPTIONS,
	USA_STATE_OPTIONS_MAP,
	TIMEFRAME_TYPE_OPTIONS,
	BUSINESS_ENTITY_OPTIONS,
	INDUSTRY_OPTIONS,
	INDUSTRY_OPTIONS_MAP,
	FICO_SCORE_TYPE_OPTIONS,
	FICO_SCORE_TYPE_OPTIONS_MAP,
	FUNDING_PURPOSE_OPTIONS,
	YES_OR_NO_TYPE_OPTIONS,
	MARTIAL_STATUS_OPTIONS,
	REAL_ESTATE_OPTIONS,
	CITIZENSHIP_STATUS_OPTIONS,
};
